import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarPlus, FileText, CheckCircle2, ClipboardList, ShoppingCart, PackageCheck, FileSpreadsheet,
  ChevronRight, AlertCircle,
} from 'lucide-react'
import { usePmsDashboard } from './dashboard/hooks/usePmsDashboard'
import QuickActions from './dashboard/QuickActions'
import KpiCards from './dashboard/KpiCards'
import OverdueJobsTable from './dashboard/OverdueJobsTable'
import UpcomingJobsTable from './dashboard/UpcomingJobsTable'
import RunningHourGauges from './dashboard/RunningHourGauges'
import ProcurementWidgets from './dashboard/ProcurementWidgets'
import ProcurementAdditionalWidgets from './dashboard/ProcurementAdditionalWidgets'
import {
  KpiDetailModal,
  OverdueJobsModal,
  UpcomingJobsModal,
  RunningHoursModal,
  ProcurementDetailModal,
} from './dashboard/DashboardModals'
import DashboardModal from './dashboard/DashboardModal'
import CardInfoButton from './dashboard/CardInfoButton'
import type { CardMeta, ModalType } from './dashboard/types'

const PmsDashboard: FC = () => {
  const navigate = useNavigate()
  const { loading, data, kpis, runningHourCounters, refresh } = usePmsDashboard()

  // ── Modal state ──
  const [modal, setModal] = useState<ModalType>(null)
  const [actionModal, setActionModal] = useState<string | null>(null)
  const openModal = (m: ModalType) => setModal(m)
  const closeModal = () => setModal(null)

  // ── Quick action routing ──
  const actionRoutes: Record<string, { label: string; route: string }> = {
    scheduleJob: { label: 'Schedule Job', route: '/technical/pms/calendar-planning' },
    createJobCard: { label: 'Create Job Card', route: '/technical/pms/jobs' },
    closeJob: { label: 'Close Job', route: '/technical/pms/jobs?state=CLOSED' },
    raiseRequisition: { label: 'Raise Requisition', route: '/procurement/requisition' },
    createPO: { label: 'Create PO', route: '/procurement/purchase-orders' },
    goodsReceipt: { label: 'Goods Receipt', route: '/procurement/goods-receipt' },
    uploadMachinery: { label: 'Upload Bulk Machinery Sheet', route: '/technical/machinery/upload' },
  }

  // ── Sidebar panels ──
  const actionCenterMeta: CardMeta = {
    description: 'Summary of items requiring attention: overdue jobs, critical equipment, pending defects and postponements, and counters never started.',
    endpoint: 'GET /pms/dashboard/action-center',
    logic: 'Displays Action Center stats from the API.',
  }
  const procurementMeta: CardMeta = {
    description: 'Quick statistics for open requisitions, purchase orders, and GRN records.',
    endpoint: 'GET /procurement/{purchase-orders|requisitions|goods-receipts}',
    logic: 'Aggregates total counts from procurement API paged responses.',
  }

  const quickActionsGrid = [
    { label: 'Schedule Job', sub: 'Plan maintenance', icon: CalendarPlus, bg: 'bg-orange-100', iconColor: 'text-orange-600', action: 'scheduleJob' },
    { label: 'Create Job Card', sub: 'Create new job', icon: FileText, bg: 'bg-blue-100', iconColor: 'text-blue-600', action: 'createJobCard' },
    { label: 'Close Job', sub: 'Mark job as complete', icon: CheckCircle2, bg: 'bg-emerald-100', iconColor: 'text-emerald-600', action: 'closeJob' },
    { label: 'Raise Requisition', sub: 'Request items', icon: ClipboardList, bg: 'bg-orange-100', iconColor: 'text-orange-600', action: 'raiseRequisition' },
    { label: 'Create PO', sub: 'Create purchase order', icon: ShoppingCart, bg: 'bg-amber-100', iconColor: 'text-amber-600', action: 'createPO' },
    { label: 'Goods Receipt', sub: 'Record received items', icon: PackageCheck, bg: 'bg-sky-100', iconColor: 'text-sky-600', action: 'goodsReceipt' },
    { label: 'Upload Bulk Machinery Sheet', sub: 'Import Bulk Machinery Sheet', icon: FileSpreadsheet, bg: 'bg-teal-100', iconColor: 'text-teal-600', action: 'uploadMachinery' },
  ]

  const handleKpiClick = (kpi: string) => {
    switch (kpi) {
      case 'overdueJobs':
        navigate('/technical/pms/jobs?state=OVERDUE')
        break
      case 'closedJobs':
        navigate('/technical/pms/jobs?state=SCHEDULED_COMPLETED')
        break
      case 'totalJobs':
        navigate('/technical/pms/jobs')
        break
      case 'unplannedOverdue':
        navigate('/technical/pms/jobs?state=DUE_BY_DEFECT')
        break
      case 'pendingDefectRequests':
        navigate('/technical/pms/jobs?tab=requests')
        break
      case 'pendingPostponements':
        navigate('/technical/pms/jobs?state=DEFERRED')
        break
      default:
        openModal('kpiDetail')
        break
    }
  }

  return (
    <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5'>
      {/* Quick Actions Header */}
      <QuickActions
        onAction={(a) => {
          const r = actionRoutes[a]?.route
          if (r) navigate(r)
        }}
      />

      {/* Quick Actions Grid */}
      <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3'>
        {quickActionsGrid.map((btn) => (
          <button
            key={btn.action}
            className='flex flex-col items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer'
            onClick={() => {
              const r = actionRoutes[btn.action]?.route
              if (r) navigate(r)
            }}
          >
            <div className={`w-10 h-10 rounded-full ${btn.bg} flex items-center justify-center`}>
              <btn.icon className={`w-5 h-5 ${btn.iconColor}`} />
            </div>
            <div className='text-center'>
              <div className='text-[11px] font-medium text-slate-800'>{btn.label}</div>
              <div className='text-[9px] text-slate-400'>{btn.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <KpiCards
        loading={loading}
        kpis={kpis}
        actionCenter={data.actionCenter}
        onKpiClick={handleKpiClick}
      />

      {/* Running Hours Counters */}
      <RunningHourGauges counters={runningHourCounters} onViewAll={() => navigate('/technical/pms/running-hours')} />

      {/* Data Tables + Sidebar */}
      <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
        <div className='xl:col-span-9 flex flex-col gap-5 md:gap-6'>
          {/* Top row: Overdue, Upcoming, POs */}
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6'>
            <div className='flex flex-col'>
              <OverdueJobsTable jobs={data.overdueJobs} loading={loading} totalCount={data.overdueTotalCount} onViewAll={() => navigate('/technical/pms/jobs?state=OVERDUE')} />
            </div>
            <div className='flex flex-col'>
              <UpcomingJobsTable jobs={data.upcomingJobs} loading={loading} totalCount={data.upcomingTotalCount} onViewAll={() => navigate('/technical/pms/jobs?state=SCHEDULED_20_PERCENT')} />
            </div>
            <div className='flex flex-col'>
              <ProcurementWidgets
                recentPOs={data.recentPOs} recentReqs={[]} recentGRNs={[]}
                loading={loading} openPRCount={0} openPOCount={data.openPOCount} grnPendingCount={0}
                onViewPOs={() => navigate('/procurement/purchase-orders')} onViewReqs={() => {}} onViewGRNs={() => {}}
              />
            </div>
          </div>

          {/* Requisitions & GRN */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6'>
            <div className='flex flex-col'>
              <ProcurementWidgets
                recentPOs={[]} recentReqs={data.recentReqs} recentGRNs={[]}
                loading={loading} openPRCount={data.openPRCount} openPOCount={0} grnPendingCount={0}
                onViewPOs={() => {}} onViewReqs={() => navigate('/procurement/requisition')} onViewGRNs={() => {}}
              />
            </div>
            <div className='flex flex-col'>
              <ProcurementWidgets
                recentPOs={[]} recentReqs={[]} recentGRNs={data.recentGRNs}
                loading={loading} openPRCount={0} openPOCount={0} grnPendingCount={data.grnPendingCount}
                onViewPOs={() => {}} onViewReqs={() => {}} onViewGRNs={() => navigate('/procurement/goods-receipt')}
              />
            </div>
          </div>
          <ProcurementAdditionalWidgets
            rfqs={data.recentRFQs}
            quotations={data.recentQuotations}
            invoices={data.recentInvoices}
            rfqCount={data.activeRfqCount}
            quotationCount={data.pendingQuotationCount}
            invoiceCount={data.pendingInvoiceCount}
            loading={loading}
            onRFQs={() => navigate('/procurement/rfq-management')}
            onQuotations={() => navigate('/procurement/quotation-management')}
            onInvoices={() => navigate('/procurement/invoice-management')}
          />
        </div>

        {/* Sidebar Panel */}
        <div className='xl:col-span-3 flex flex-col gap-5 md:gap-6'>
          {/* Action Center Summary */}
          <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
            <div className='flex items-center justify-between'>
              <div className='text-[10px] text-slate-500 mb-1'>Action Center</div>
              <CardInfoButton meta={actionCenterMeta} />
            </div>
            <div className='text-xs font-semibold text-slate-700 mb-3'>Requires Attention</div>
            <div className='space-y-3'>
              <button
                type='button'
                onClick={() => navigate('/technical/pms/jobs?state=OVERDUE')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-red-50/50 transition-colors'
              >
                <span className='text-slate-600'>Overdue Jobs</span>
                <span className='font-bold text-red-600'>
                  {loading ? <span className='inline-block w-6 h-3 bg-red-100 rounded animate-pulse' /> : (kpis.overdueJobs ?? data.actionCenter?.overdueJobs ?? 0)}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/technical/machinery')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-amber-50/50 transition-colors'
              >
                <span className='text-slate-600'>Critical Equipments</span>
                <span className='font-bold text-amber-600'>
                  {loading ? <span className='inline-block w-6 h-3 bg-amber-100 rounded animate-pulse' /> : (data.actionCenter?.criticalEquipments ?? 0)}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/technical/pms/jobs?tab=requests')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-rose-50/50 transition-colors'
              >
                <span className='text-slate-600'>Pending Defect Requests</span>
                <span className='font-bold text-rose-600'>
                  {loading ? <span className='inline-block w-6 h-3 bg-rose-100 rounded animate-pulse' /> : (data.actionCenter?.pendingDefectRequests ?? 0)}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/technical/pms/jobs?state=DEFERRED')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-indigo-50/50 transition-colors'
              >
                <span className='text-slate-600'>Pending Postponements</span>
                <span className='font-bold text-indigo-600'>
                  {loading ? <span className='inline-block w-6 h-3 bg-indigo-100 rounded animate-pulse' /> : (data.actionCenter?.pendingPostponements ?? 0)}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/technical/pms/running-hours')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-slate-100/70 transition-colors'
              >
                <span className='text-slate-600'>Counters Never Started</span>
                <span className='font-bold text-slate-600'>
                  {loading ? <span className='inline-block w-6 h-3 bg-slate-200 rounded animate-pulse' /> : (data.actionCenter?.countersNeverStarted ?? 0)}
                </span>
              </button>
            </div>
          </div>

          {/* Procurement Quick Stats */}
          <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
            <div className='flex items-center justify-between'>
              <div className='text-[10px] text-slate-500 mb-1'>Procurement</div>
              <CardInfoButton meta={procurementMeta} />
            </div>
            <div className='text-xs font-semibold text-slate-700 mb-3'>Quick Stats</div>
            <div className='space-y-3'>
              <button
                type='button'
                onClick={() => navigate('/procurement/requisition')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-slate-100/70 transition-colors'
              >
                <span className='text-slate-600'>Open Requisitions</span>
                <span className='font-bold text-slate-800'>
                  {loading ? <span className='inline-block w-6 h-3 bg-slate-200 rounded animate-pulse' /> : data.openPRCount}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/procurement/purchase-orders')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-slate-100/70 transition-colors'
              >
                <span className='text-slate-600'>Open Purchase Orders</span>
                <span className='font-bold text-slate-800'>
                  {loading ? <span className='inline-block w-6 h-3 bg-slate-200 rounded animate-pulse' /> : data.openPOCount}
                </span>
              </button>
              <button
                type='button'
                onClick={() => navigate('/procurement/goods-receipt')}
                className='flex items-center justify-between text-[11px] w-full text-left p-1.5 -mx-1.5 rounded-lg hover:bg-slate-100/70 transition-colors'
              >
                <span className='text-slate-600'>GRN Records</span>
                <span className='font-bold text-slate-800'>
                  {loading ? <span className='inline-block w-6 h-3 bg-slate-200 rounded animate-pulse' /> : data.grnPendingCount}
                </span>
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
            <div className='text-[10px] text-slate-500 mb-1'>Quick Links</div>
            <div className='space-y-2 mt-2'>
              {[
                { label: 'All PMS Jobs', route: '/technical/pms/jobs' },
                { label: 'Maintenance Plans', route: '/technical/pms/plans' },
                { label: 'Job Templates', route: '/technical/pms/templates' },
                { label: 'Running Hours', route: '/technical/pms/running-hours' },
                { label: 'Calendar Planning', route: '/technical/pms/calendar-planning' },
                { label: 'Machinery', route: '/technical/machinery' },
              ].map((link) => (
                <button
                  key={link.route}
                  onClick={() => navigate(link.route)}
                  className='text-[11px] text-blue-600 hover:underline flex items-center gap-1 w-full text-left'
                >
                  <ChevronRight className='w-3 h-3' />{link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <KpiDetailModal
        open={modal === 'kpiDetail'}
        onClose={closeModal}
        kpis={kpis}
        stats={data.stats}
        actionCenter={data.actionCenter}
      />
      <OverdueJobsModal
        open={modal === 'overdueJobs'}
        onClose={closeModal}
        jobs={data.overdueJobs}
      />
      <UpcomingJobsModal
        open={modal === 'upcomingJobs'}
        onClose={closeModal}
        jobs={data.upcomingJobs}
      />
      <RunningHoursModal
        open={modal === 'runningHours'}
        onClose={closeModal}
        counters={runningHourCounters}
      />
      <ProcurementDetailModal
        open={modal === 'procurementDetail'}
        onClose={closeModal}
        recentPOs={data.recentPOs}
        recentReqs={data.recentReqs}
        recentGRNs={data.recentGRNs}
        openPRCount={data.openPRCount}
        openPOCount={data.openPOCount}
        grnPendingCount={data.grnPendingCount}
      />

      {/* ── Quick Action Redirect Modals ── */}
      <DashboardModal
        open={!!actionModal}
        onClose={() => setActionModal(null)}
        title={actionModal ? actionRoutes[actionModal]?.label || 'Action' : ''}
      >
        <p className='text-sm text-slate-600 mb-4'>
          This will take you to the <strong>{actionModal ? actionRoutes[actionModal]?.label : ''}</strong> page.
        </p>
        <div className='flex gap-3'>
          <button
            onClick={() => { const r = actionModal ? actionRoutes[actionModal]?.route : ''; if (r) navigate(r) }}
            className='px-4 py-2 bg-blue-600 text-white rounded-lg text-[11px] font-semibold hover:bg-blue-700 transition-colors'
          >
            Go to page
          </button>
          <button
            onClick={() => setActionModal(null)}
            className='px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-[11px] font-semibold hover:bg-slate-200 transition-colors'
          >
            Cancel
          </button>
        </div>
      </DashboardModal>
    </div>
  )
}

export default PmsDashboard
